// Katalog metriği penceresinin (MetricModal) taslak modeli — DTO ↔ taslak dönüşümü (saf).
// MetricModal 600 satır sınırında olduğu için ayrı dosyada (birim varyantı Task 12). Birim
// varyantları (`unitVariants`) taslakta liste; gönderirken boşlar, varsayılanla aynı olanlar ve
// tekrarlar (normalize anahtarla) düşer — sunucu da aynı kuralı uygular.
// Kırılım (spec §6.4 DR14): `nodeKind` yalnız oluşturmada ve yalnız kategori/üye için gönderilir
// (düz gösterge eski çağıran kalıbıyla — sunucunun eski üst yükseltmesi korunur); `dimensionMember`
// üye kipinde dolu (yeni üyenin birimi üstten — `defaultUnit` boş gider); `breakdown` yalnız grup düğümünde.

import {
  normalizeUnitKey,
  type KpiAggregation,
  type KpiBreakdown,
  type KpiFormula,
  type KpiMetric,
  type KpiMetricRequirement,
  type KpiMetricUpsert,
  type KpiNodeKind,
  type KpiValueType,
} from "@mahir/shared";

export interface MetricDraft {
  label: string;
  code: string;
  parentId: string | null;
  description: string;
  defaultUnit: string;
  /** Birim varyantı: Veri Yönetimi seçicisinde "Tanımlı alternatifler" */
  unitVariants: string[];
  valueType: KpiValueType;
  isDerived: boolean;
  defaultFormulas: KpiFormula[];
  aggregation: KpiAggregation;
  /** TODO-KPI-32: yalnız weighted_avg iken anlamlı */
  aggregationWeightMetricId: string | null;
  showChangeDefault: boolean;
  increaseIsGoodDefault: boolean | null;
  defaultQuestionCode: string;
  requirements: KpiMetricRequirement[];
  /** yalnız oluşturmada gönderilir (kategori / üye); null = gönderilmez */
  nodeKind: KpiNodeKind | null;
  /** kırılım üyesi slug'ı; null = üye değil */
  dimensionMember: string | null;
  /** grup düğümünün kırılım bilgisi (tamlık / toplama); diğer düğümlerde null */
  breakdown: KpiBreakdown | null;
}

export function draftFromMetric(
  m: KpiMetric | null,
  requirements: KpiMetricRequirement[],
  preset: { kind: "metric" | "category"; parentId: string | null },
): MetricDraft {
  if (!m) {
    return {
      label: "",
      code: "",
      parentId: preset.parentId,
      description: "",
      defaultUnit: "",
      unitVariants: [],
      valueType: "number",
      isDerived: false,
      defaultFormulas: [],
      aggregation: preset.kind === "category" ? "none" : "sum",
      aggregationWeightMetricId: null,
      showChangeDefault: false,
      increaseIsGoodDefault: null,
      defaultQuestionCode: "",
      requirements: [],
      nodeKind: preset.kind === "category" ? "category" : null,
      dimensionMember: null,
      breakdown: null,
    };
  }
  return {
    label: m.label,
    code: m.code,
    parentId: m.parentId,
    description: m.description ?? "",
    defaultUnit: m.defaultUnit ?? "",
    unitVariants: [...(m.unitVariants ?? [])],
    valueType: m.valueType,
    isDerived: m.isDerived,
    defaultFormulas: m.defaultFormulas,
    aggregation: m.aggregation,
    aggregationWeightMetricId: m.aggregationWeightMetricId,
    showChangeDefault: m.showChangeDefault,
    increaseIsGoodDefault: m.increaseIsGoodDefault,
    defaultQuestionCode: m.defaultQuestionCode ?? "",
    requirements,
    nodeKind: null,
    dimensionMember: m.dimensionMember ?? null,
    breakdown: m.nodeKind === "breakdown_group" ? (m.breakdown ?? null) : null,
  };
}

/** Gönderilecek varyant listesi: boş / varsayılanla aynı / tekrar eden (normalize) düşer. */
export function cleanUnitVariants(variants: readonly string[], defaultUnit: string): string[] {
  const def = defaultUnit.trim();
  const seen = new Set<string>(def ? [normalizeUnitKey(def)] : []);
  const out: string[] = [];
  for (const raw of variants) {
    const u = raw.trim();
    if (u === "") continue;
    const k = normalizeUnitKey(u);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(u);
  }
  return out;
}

/**
 * `original` (düzenleme): kırılım alanları yalnız DEĞİŞTİYSE gider — grup metaverisi yaması
 * sunucuda dönüşüm etkisi taşır (R10), her etiket kaydında yeniden yazılmamalı.
 */
export function draftToUpsert(d: MetricDraft, original?: MetricDraft): KpiMetricUpsert {
  const member = d.dimensionMember !== null;
  // yeni üye birimsiz doğar (üstten devralır); mevcut düğümün birimi bu yoldan değiştirilmez
  const inheritUnit = member && !original;
  const memberChanged = original?.dimensionMember !== d.dimensionMember;
  const breakdownChanged =
    !original || JSON.stringify(original.breakdown) !== JSON.stringify(d.breakdown);
  return {
    label: d.label.trim(),
    ...(d.code.trim() ? { code: d.code.trim() } : {}),
    parentId: d.parentId,
    description: d.description.trim() || null,
    defaultUnit: inheritUnit ? null : d.defaultUnit.trim() || null,
    unitVariants: inheritUnit ? [] : cleanUnitVariants(d.unitVariants, d.defaultUnit),
    valueType: d.valueType,
    isDerived: d.isDerived,
    defaultFormulas: d.isDerived ? d.defaultFormulas : [],
    aggregation: d.aggregation,
    aggregationWeightMetricId:
      d.aggregation === "weighted_avg" ? d.aggregationWeightMetricId : null,
    showChangeDefault: d.showChangeDefault,
    increaseIsGoodDefault: d.increaseIsGoodDefault,
    defaultQuestionCode: d.defaultQuestionCode.trim() || null,
    requirements: d.requirements.filter((r) => r.standard.trim() && r.code.trim()),
    ...(d.nodeKind ? { nodeKind: d.nodeKind } : {}),
    ...(member && memberChanged ? { dimensionMember: d.dimensionMember } : {}),
    ...(d.breakdown && breakdownChanged ? { breakdown: d.breakdown } : {}),
  };
}
